import {
    AddIcon,
    BulletsIcon,
    Button,
    CloseIcon,
    EditIcon,
    Flex,
    FlexItem,
    Header,
    List,
    Loader,
    MenuButton,
    MoreIcon,
    Text,
    TrashCanIcon
} from "@fluentui/react-northstar";
import languageObject from "../../translations";
import * as React from "react";
import {useCallback, useContext, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {ImportDialog} from "./ImportDialog/ImportDialog";
import {MailingList} from "../../Interfaces/MailingList";
import "./usersLists.scss";
import ConfirmDialog from "../Dialogs/ConfirmDialog";
import {AppContext, IAppContextState} from "../../services/AppContext";

type State = {
    showImportDialog: boolean,
    deletingListId: string,
    showConfirmDelete: boolean,
    isDeleting: boolean,
}

const initialState: State = {
    showImportDialog: false,
    deletingListId: "",
    showConfirmDelete: false,
    isDeleting: false,
}

export const MailingLists = () => {
    const navigate = useNavigate();
    const context = useContext<IAppContextState>(AppContext);
    const [state, setState] = useState<State>({...initialState});

    const {loaded, lists} = context.usersLists;

    const handleToggleShowImportDialog = useCallback(() => {
        setState((prevState) => ({...prevState, showImportDialog: !prevState.showImportDialog}));
    }, []);

    const handleEditList = useCallback((list: MailingList) => {
        navigate(`/users-lists/${list.id}`);
    }, []);

    const handleDeleteList = useCallback((list: MailingList) => {
        setState(prevState => ({...prevState, showConfirmDelete: true, deletingListId: list.id}));
    }, []);

    const handleConfirmDeleteList = useCallback((deletingListId: string) => async () => {
        setState(prevState => ({...prevState, isDeleting: true}));
        await context.usersLists.deleteUsersList(deletingListId);
        setState(prevState => ({
            ...prevState,
            isDeleting: false,
            showConfirmDelete: false,
            deletingListId: "",
        }));
    }, []);

    const handleClose = useCallback(() => {
        navigate("/messages");
    }, []);

    const handleCancelDeleteList = useCallback(() => {
        setState(prevState => ({...prevState, isDeleting: false, deletingListId: "", showConfirmDelete: false}));
    }, []);

    const importDialog = (
        <ImportDialog onClose={handleToggleShowImportDialog} open={state.showImportDialog}/>
    );

    const items = useMemo(() => lists.map(list => renderItem({
        list,
        deletingListId: state.deletingListId,
        onEditList: handleEditList,
        onDeleteList: handleDeleteList,
    })), [lists]);

    return (
        <Flex column fill gap="gap.medium" className={"p-20px"}>
            <Flex gap={"gap.small"}>
                <Flex gap={"gap.small"} vAlign={"center"}>
                    <BulletsIcon styles={{color: "#782753"}} size={"large"}/>
                    <Header as={"h2"} styles={{margin: "0"}}
                            content={languageObject.translate("ManagementOfMailingLists")}/>
                </Flex>
                <FlexItem push>
                    <Button
                        primary
                        icon={<AddIcon outline/>}
                        content={languageObject.translate("ImportList")}
                        onClick={handleToggleShowImportDialog}
                    />
                </FlexItem>
                <Button
                    icon={<CloseIcon outline/>}
                    content={languageObject.translate("CloseText")}
                    onClick={handleClose}
                />
            </Flex>
            <Flex fill vAlign={"center"} hAlign={"center"}>
                {!loaded ?
                    <Flex fill vAlign={"center"} hAlign={"center"}>
                        <Loader/>
                    </Flex>
                    :
                    items.length > 0 ?
                        <List selectable items={items} className="users_lists_list" truncateContent truncateHeader/>
                        :
                        <Flex fill styles={{marginTop: "10px", marginLeft: "10px"}}>
                            <Text weight={"bold"} content={languageObject.translate("EmptyLists")}/>
                        </Flex>
                }
                <ConfirmDialog
                    show={state.showConfirmDelete}
                    title={languageObject.translate("RemoveMailingList")}
                    subtitle={lists.find(l => l.id === state.deletingListId)?.name}
                    content={languageObject.translate("ConfirmRemoveMailingList")}
                    onCancel={handleCancelDeleteList}
                    onConfirm={handleConfirmDeleteList(state.deletingListId)}
                    loading={state.isDeleting}
                    button={{
                        icon: <TrashCanIcon outline/>,
                        title: languageObject.translate("Remove"),
                        className: "delete_button",
                    }}
                />
            </Flex>
            {importDialog}
        </Flex>
    );
}

const renderItem = (data: {
    list: MailingList,
    deletingListId: string,
    onEditList: (list: MailingList) => void,
    onDeleteList: (list: MailingList) => void,
}) => {
    const {list, onEditList, deletingListId, onDeleteList} = data;
    const subTitle = languageObject.translate(list.emails.length < 2 ? "CountUser" : "CountUsers", {count: list.emails.length});
    return {
        key: list.id,
        content: (
            <Flex vAlign="center" fill gap="gap.small">
                <Flex column fill>
                    <Text weight={"semibold"} truncated>{list.name}</Text>
                    <Text truncated>{subTitle}</Text>
                </Flex>
                <Flex.Item push>
                    <MenuButton
                        className={"menuContainer"}
                        trigger={<MenuButtonTrigger onClick={e => e.stopPropagation()}/>}
                        menu={[
                            {
                                key: 'edit',
                                icon: <EditIcon outline/>,
                                content: languageObject.translate("Edit"),
                                onClick: (event: any) => {
                                    event.stopPropagation();
                                    if (list.id) onEditList(list);
                                }
                            },
                            {
                                key: 'delete',
                                icon: <TrashCanIcon outline/>,
                                content: languageObject.translate("Delete"),
                                onClick: (event: any) => {
                                    event.stopPropagation();
                                    onDeleteList(list);
                                }
                            },
                        ]}/>
                </Flex.Item>
            </Flex>
        ),
        styles: {margin: '0.3rem 0.3rem 0 0'},
        onClick: () => {
            if (list.id && deletingListId !== list.id) onEditList(list);
        },
    }
}

function MenuButtonTrigger(props: {
    onClick: (e: any) => void
}) {
    const [isHovering, setHovering] = useState(true);

    return (
        <Flex styles={{padding: "7px"}}
              onMouseEnter={() => setHovering(false)}
              onMouseLeave={() => setHovering(true)}
              onClick={props.onClick}>
            <MoreIcon className={"list-more-icon"} outline={isHovering}/>
        </Flex>
    )
}